// src/lib/labels.js — THE display dictionary (designer rule 2026-07-23, QUESTIONS.md
// "MOBILE + VOCAB"): no normalized token ever reaches the screen. Every enum value that
// renders passes through here → one Georgian/English label via i18n. This retires the
// three scattered intent maps (HistoryCards LISTING_INTENT_LABEL_KEYS, PersonModal
// INTENT_LABEL_KEYS, ManualClientModal INTENT_OPTION_KEYS) — add new enum labels HERE,
// never as a component-local map.
// Unknown values degrade to underscores→spaces (same rule as humanMatchKey, 2026-07-13
// audit): an unmapped value must never render as an engineer token, and never crash.
import { t } from './i18n.js'

const LISTING_INTENT_KEYS = {
  sale: 'desk_listing_intent_sale', rent: 'desk_listing_intent_rent', daily_rent: 'desk_listing_intent_daily',
  // 0219: girao is a deal type on ss/myhome/home.ge (only korter lacks it), not a
  // cheap sale. ijara (myhome deal type 10) gets a label and nothing more — we don't
  // broker those, but until it had one every ijara ad rendered as "For sale".
  girao: 'desk_listing_intent_girao', ijara: 'desk_listing_intent_ijara',
}
// Demand side uses the NOUN forms (designer 2026-07-23: ყიდვა/ქირაობა/…) — the
// desk_intent_opt_* family, extended with the previously-unmapped invest/lease/sale.
const DEMAND_INTENT_KEYS = {
  buy: 'desk_intent_opt_buy', invest: 'desk_intent_opt_invest', rent: 'desk_intent_opt_rent',
  lease: 'desk_intent_opt_lease', buy_or_rent: 'desk_intent_opt_buy_or_rent',
  short_term_rent: 'desk_intent_opt_short_term_rent', sale: 'desk_intent_opt_sale',
  // Demand side of girao (0219, requirement_intent_check widened): a client looking
  // to PUT UP a gira sum. Same word both sides — Georgian has no separate verb here
  // the way ყიდვა/გაყიდვა splits buy from sell.
  girao: 'desk_intent_opt_girao',
}
// property.kind === requirement.property_category (one taxonomy, scorer 0.7.x).
// Existing desk_category_opt_* keys are reused; the designer-dictated gaps get desk_kind_*.
const KIND_KEYS = {
  apartment: 'desk_category_opt_apartment', penthouse: 'desk_kind_penthouse',
  house: 'desk_category_opt_house', townhouse: 'desk_kind_townhouse', duplex: 'desk_kind_duplex',
  cottage: 'desk_category_opt_cottage', land: 'desk_category_opt_land',
  commercial: 'desk_category_opt_commercial', office: 'desk_category_opt_office',
  shop: 'desk_kind_shop', cafe: 'desk_kind_cafe', warehouse: 'desk_kind_warehouse',
  garage: 'desk_category_opt_garage', basement: 'desk_kind_basement',
  commercial_special: 'desk_kind_commercial_special',
  hotel: 'desk_category_opt_hotel', other: 'desk_category_opt_other',
}
const STATUS_KEYS = {
  active: 'desk_status_val_active', paused: 'desk_status_val_paused',
  withdrawn: 'desk_status_val_withdrawn', sold: 'desk_status_val_sold',
  rented: 'desk_status_val_rented', expired: 'desk_status_val_expired',
}
// property_feature.source_kind + the event stream's source_kind — WHO recorded a fact.
// The CONFLICT popover labels each disagreeing value with this, and it is the only provenance
// those rows have: property_feature is keyed (property, feature, source_kind) and keeps no
// link to the raw_listing it was projected from, so "which portal" is not answerable — hence
// a KIND here, never a platform name.
const SOURCE_KIND_KEYS = {
  scrape: 'desk_source_kind_scrape', agent: 'desk_source_kind_agent',
  owner: 'desk_source_kind_owner', registry: 'desk_source_kind_registry',
  ai: 'desk_source_kind_ai', admin: 'desk_source_kind_admin',
}

const label = (map, v) => {
  if (v == null || v === '') return ''
  const key = map[v]
  if (!key) return String(v).replace(/_/g, ' ')
  const s = t(key)
  return s === key ? String(v).replace(/_/g, ' ') : s
}

export const intentLabel = (v, side = 'listing') => label(side === 'demand' ? DEMAND_INTENT_KEYS : LISTING_INTENT_KEYS, v)
export const kindLabel = v => label(KIND_KEYS, v)
export const statusLabel = v => label(STATUS_KEYS, v)
export const sourceKindLabel = v => label(SOURCE_KIND_KEYS, v)

// Claim attributes (claim.attribute) — mapped BY CONVENTION (desk_attr_<attribute>) rather
// than through an enumerated map, because unlike every other dictionary above this one is
// OPEN: claim.attribute is free text (core 0001), so ingestion can add a fact server-side
// without a migration and has done so repeatedly (year_built, the area breakdown,
// metro_station). An enumerated map would silently fall back to the raw token for each new
// one. Same degradation as `label`: unknown → humanised column name, never a crash.
// ⚠️ Until 2026-08-06 the my-version panel called `attr.replace(/_/g,' ')` inline, which is
// exactly what this module's header forbids — that is how "floors total" and "area sqm"
// reached an all-Georgian screen.
export const attrLabel = v => {
  if (v == null || v === '') return ''
  const key = `desk_attr_${v}`
  const s = t(key)
  return s === key ? String(v).replace(/_/g, ' ') : s
}
